// SPDX-FileCopyrightText: Copyright 2024 Dash0 Inc.

"use client";

import SearchedTextHighlighterWithoutAutoExpansion from "@/components/shared/search/search-text-highlighter-without-auto-expansion";
import Link from "next/link";
import { BetaTag } from "@/components/ui/beta-tag";
import { ExtendedIntegrationMetadata } from "@/hub/types";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import clsx from "clsx";

type IntegrationCardProps = {
  integration: ExtendedIntegrationMetadata;
  subIntegrations: ExtendedIntegrationMetadata[];
  prefetch?: boolean;
  forceOpen?: boolean;
  useSpanForTitles?: boolean;
};

const hubIntegration = "hub.integration";

export function IntegrationCard({
  integration,
  subIntegrations,
  prefetch,
  forceOpen = false,
  useSpanForTitles = false
}: IntegrationCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const MainHeading = useSpanForTitles ? "span" : "h2";
  const SubHeading = useSpanForTitles ? "span" : "h3";

  useEffect(() => {
    setIsOpen(forceOpen);
  }, [forceOpen]);

  let content = (
    <div
      className={clsx(
        "group relative flex bg-[#222224] hover:bg-[#27272a] rounded-md",
        subIntegrations.length > 0 &&
          isOpen &&
          "rounded-bl-none rounded-br-none bg-[#27272a]"
      )}
    >
      <Link
        href={`/hub/integrations/${integration.id}/overview`}
        className={cn(
          "group flex-1 max-h-fit group relative flex items-center gap-3  h-full p-3 transition-colors",
          "ring-0"
        )}
        prefetch={prefetch}
        data-testid={hubIntegration}
      >
        <div
          dangerouslySetInnerHTML={{ __html: integration.icon }}
          className="h-12 w-12 shrink-0 rounded-sm flex items-center justify-center border-[0.8px] group-hover:bg-[#303034] bg-[#303033] border-[#414144] transition-colors [&_svg]:h-[28px] [&_svg]:w-[28px]"
        />

        <MainHeading className="flex items-center gap-2 text-base font-medium leading-normal transition-colors m-0">
          <SearchedTextHighlighterWithoutAutoExpansion
            text={integration.label}
          />
          {integration.beta && <BetaTag />}
        </MainHeading>
      </Link>

      {subIntegrations.length > 0 && (
        <button
          onClick={(e) => {
            e.preventDefault();
            setIsOpen(!isOpen);
          }}
          className="h-full px-3 bg-transparent right-2 absolute flex items-center transition-colors"
        >
          <svg
            className={cn(
              "w-5 h-5 transition-transform duration-200 text-grey-400",
              isOpen ? "rotate-180" : ""
            )}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </div>
  );

  if (subIntegrations.length > 0) {
    content = (
      <div className="rounded-md">
        {content}
        <div
          className={cn(
            "transition-all duration-200 overflow-hidden",
            isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
          )}
        >
          {subIntegrations.map((subIntegration) => (
            <Link
              key={subIntegration.id}
              href={`/hub/integrations/${subIntegration.id}/overview`}
              className="max-h-fit group bg-[#1f1f22] not-last:pb-0!  py-4 flex items-center last-of-type:rounded-br-md last-of-type:rounded-bl-md gap-3 p-3 pl-9 transition-colors"
              prefetch={prefetch}
              data-testid={hubIntegration}
            >
              <div
                dangerouslySetInnerHTML={{ __html: subIntegration.icon }}
                className="h-6 w-6 shrink-0 rounded-sm border-[0.7px] border-[#414144] bg-[#303033] p-1 transition-colors group-hover:bg-lvl-3-hover [&_svg]:h-full [&_svg]:w-full"
              />

              <SubHeading className="flex items-center gap-2 text-sm leading-normal text-lvl-1-intense transition-colors group-hover:text-lvl-2-intense m-0">
                <SearchedTextHighlighterWithoutAutoExpansion
                  text={subIntegration.label}
                />
                {subIntegration.beta && <BetaTag />}
              </SubHeading>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return content;
}
